import { ArrowRight, Box, ChevronDown, GitBranch, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { diffValue, type SchemaDiffRow } from './proposal-diff'
import type { SchemaDiffGroup } from './proposal-diff-groups'
import { AskSchemaAgent } from './SchemaConversationContext'

const GROUP_ICONS = {
  entity: Box,
  relationship: ArrowRight,
  pattern: GitBranch,
  schema: SlidersHorizontal,
}
const OPERATION_VARIANTS = { add: 'success', remove: 'destructive', change: 'info' } as const

export function DiffOperationBadge({
  operation,
  count,
}: {
  operation: SchemaDiffRow['operation']
  count?: number
}) {
  const { t } = useTranslation()
  return (
    <Badge variant={OPERATION_VARIANTS[operation]}>
      {t(`schemaAgent.diff.${operation}`)}
      {count !== undefined && ` ${count}`}
    </Badge>
  )
}

export function ProposalDiffGroups({ groups }: { groups: SchemaDiffGroup[] }) {
  return (
    <div className="space-y-3">
      {groups.map(group => (
        <DiffGroup key={group.target} group={group} openInitially={groups.length === 1} />
      ))}
    </div>
  )
}

function DiffGroup({ group, openInitially }: { group: SchemaDiffGroup; openInitially: boolean }) {
  const { t } = useTranslation()
  const Icon = GROUP_ICONS[group.kind]
  return (
    <Collapsible
      defaultOpen={openInitially}
      className="group/diff overflow-hidden rounded-lg border"
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none p-3 text-left whitespace-normal"
        >
          <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block break-words font-medium">{group.label}</span>
            <span className="block text-xs font-normal text-muted-foreground">
              {t(`schemaAgent.diff.kind.${group.kind}`)} ·{' '}
              {t('schemaAgent.diff.details', { count: group.rows.length })}
            </span>
          </span>
          <DiffOperationBadge operation={group.operation} />
          <ChevronDown
            className="size-4 shrink-0 text-muted-foreground motion-safe:transition-transform group-data-[state=open]/diff:rotate-180"
            aria-hidden
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t">
        <div className="px-3">
          <AskSchemaAgent label={group.label} scope="proposal" />
        </div>
        <div className="divide-y">
          {group.rows.map(row => (
            <DiffField key={row.target} row={row} group={group} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function DiffField({ row, group }: { row: SchemaDiffRow; group: SchemaDiffGroup }) {
  const { t } = useTranslation()
  const label =
    row.target === group.target
      ? t('schemaAgent.diff.definition')
      : row.target.slice(group.target.length + 1)
  return (
    <section className="text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-3 py-2">
        <h4 className="break-all font-mono font-medium">{label}</h4>
        <DiffOperationBadge operation={row.operation} />
      </div>
      <div className="grid grid-cols-1 divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <DiffValue side="before" value={row.before} />
        <DiffValue side="after" value={row.after} />
      </div>
    </section>
  )
}

function DiffValue({ side, value }: { side: 'before' | 'after'; value: unknown }) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0 p-3">
      <p className="mb-2 text-muted-foreground">{t(`schemaAgent.diff.${side}`)}</p>
      <pre className="whitespace-pre-wrap break-words font-mono leading-relaxed">
        {diffValue(value)}
      </pre>
    </div>
  )
}
